/**
 * The app on an old address (pwa/move.ts): instead of starting, it retires the service
 * worker, carries the player's data across and goes to the same page on the site. From
 * the home screen it stays put and says Sparrow has moved (components/Moved.tsx).
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { MovedScreen } from '../components/Moved.tsx';
import { LoadingState } from '../components/States.tsx';
import { SITE_URL } from '../env.ts';
import { platformStore } from '../platform/config.ts';
import { exitLink } from '../pwa/guide.ts';
import { carryForMove, type HandOver, hasDataToCarry, movedUrl, retireServiceWorker } from '../pwa/move.ts';
import { readPlatform } from '../pwa/platform.ts';

/** A code lives 30 minutes: a home-screen app left open longer than this makes a fresh one. */
const CODE_FRESH_MS = 20 * 60 * 1000;

export async function leaveOldAddress(el: HTMLElement): Promise<void> {
	const root = createRoot(el);
	const host = new URL(SITE_URL).host;
	root.render(
		<StrictMode>
			<LoadingState label={`Moving to ${host}`} />
		</StrictMode>,
	);
	const started = Date.now();
	await retireServiceWorker();
	const store = platformStore;
	const platform = readPlatform();
	const has = hasDataToCarry(store);
	const code = has ? await carryForMove(store) : null;
	const handOver: HandOver = has ? (code ? { code } : { failed: true }) : null;
	if (!platform.standalone) {
		// Its data stays here too, so coming back to this address hands it over again.
		window.location.replace(movedUrl(SITE_URL, window.location.href, handOver));
		return;
	}
	const link = movedUrl(SITE_URL, window.location.href, handOver, true);
	// Out to the browser itself (Safari, Chrome), where it can be added to the home screen again.
	const href = platform.ios || platform.android ? exitLink(link, platform.android) : link;
	root.render(
		<StrictMode>
			<MovedScreen host={host} href={href} plain={link} carry={!has ? 'none' : code ? 'carried' : 'failed'} onRetry={() => window.location.reload()} />
		</StrictMode>,
	);
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible' && code && Date.now() - started > CODE_FRESH_MS) window.location.reload();
	});
}
